import { Table, Text, Tooltip } from '@mantine/core';
import type { ColumnConfig } from '../../../hooks/useColumnConfig';
import type { ColumnAverages } from '../../../types';
import { averageOf, formatAverage } from '../../../utils/columnAverages';
import classes from './ConfigurableTable.module.css';

interface ColumnAveragesRowProps<T> {
  /** The visible columns, in display order */
  columns: ColumnConfig[];
  /** Server averages over all matching rows, by column id */
  averages: ColumnAverages;
  /** The rows on screen */
  rows: T[];
  /** All matching rows, across every page */
  totalCount: number;
  /** A row's raw value for a column. Defaults to the field named like the column id. */
  getValue?: (row: T, columnId: string) => unknown;
  /** Class for the first cell, so a sticky first column stays sticky (classes.stickyColumn) */
  firstCellClassName?: string;
}

const defaultGetValue = (row: unknown, columnId: string) =>
  (row as Record<string, unknown>)[columnId];

/**
 * MDF-41: one summary row directly under the column headers. Render it as
 * the first body row; inside the sticky header it would cover the sticky
 * group-by header (top: 44px). Each numeric
 * column shows the average of all matching rows, and the average of the rows
 * on screen when pagination hides some rows. Blank cells are not counted.
 */
export function ColumnAveragesRow<T>({
  columns,
  averages,
  rows,
  totalCount,
  getValue = defaultGetValue,
  firstCellClassName,
}: ColumnAveragesRowProps<T>) {
  if (rows.length === 0 || !columns.some(col => col.numeric)) return null;
  const showPage = totalCount > rows.length;

  return (
    <Table.Tr className={classes.averagesRow}>
      {columns.map((col, index) => (
        <Table.Td key={col.id} className={index === 0 ? firstCellClassName : undefined}>
          {col.numeric ? (
            <>
              <Tooltip
                label={`Average of all ${totalCount} matching rows. Blank cells are not counted.`}
                withArrow
                multiline
                maw={260}
              >
                <Text component="span" display="block" fz={12} fw={600}>
                  Avg {formatAverage(averages[col.id], col.numeric)}
                </Text>
              </Tooltip>
              {showPage && (
                <Tooltip label="Average of the rows on this page. Blank cells are not counted." withArrow>
                  <Text component="span" display="block" fz={11} c="dimmed">
                    Page {formatAverage(averageOf(rows.map(row => getValue(row, col.id))), col.numeric)}
                  </Text>
                </Tooltip>
              )}
            </>
          ) : index === 0 ? (
            <Text component="span" fz={12} fw={600} c="dimmed">
              Averages
            </Text>
          ) : null}
        </Table.Td>
      ))}
    </Table.Tr>
  );
}
